import { apiUrl } from "../constants/const";
import logger from "../services/Logger";
import useSessionIdStore from "../store/useSessionIdStore";

/**
 * Fetch pending approvals for a chat.
 * 
 * Used to rehydrate approval cards on page reload.
 * 
 * @param {string} chatId - The chat ID to fetch pending approvals for
 * @returns {Promise<Array>} Array of pending approval data
 */
export const getPendingApprovals = async (chatId) => {
  const sessionToken = localStorage.getItem("session_token");

  const response = await fetch(`${apiUrl}/approval/pending/${chatId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-Session-Token": sessionToken || "",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to fetch pending approvals: ${response.status}`);
  }

  const data = await response.json();
  return data.pending_approvals || [];
};

/**
 * Respond to an approval request (approve or cancel).
 * 
 * Cancel/Approve: Both return and emit ADD_MESSAGE event for the result message.
 * Frontend should rely on ADD_MESSAGE listener to display the outcome.
 * 
 * @param {Object} params
 * @param {string} params.approvalId - The approval ID to respond to
 * @param {string} params.action - "approve" or "cancel"
 * @param {string} params.confirmationText - Required for approve action
 * @param {string} params.chatId - The chat that owns the approval
 */
export const respondToApproval = async ({
  approvalId,
  action,
  confirmationText,
  chatId,
}) => {
  const sessionToken = localStorage.getItem("session_token");

  const response = await fetch(`${apiUrl}/approval/${approvalId}/respond`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Session-Token": sessionToken || "",
    },
    body: JSON.stringify({
      action,
      confirmation_text: confirmationText,
      chat_id: chatId,
      session_id: useSessionIdStore.getState().session_id,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Approval response failed: ${response.status}`);
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json();
  }

  if (action === "approve" && response.body) {
    return response.body.getReader();
  }
  
  return null;
};

/**
 * Mark an approval as expired.
 * 
 * Called when the frontend timer expires.
 * System message will appear via ADD_MESSAGE listener automatically.
 * 
 * @param {string} approvalId - The approval ID that expired
 * @param {string} chatId - The chat that owns the approval
 */
export const markApprovalExpired = async (approvalId, chatId) => {
  const sessionToken = localStorage.getItem("session_token");

  try {
    const response = await fetch(`${apiUrl}/approval/${approvalId}/expire`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Session-Token": sessionToken || "",
      },
      body: JSON.stringify({
        chat_id: chatId,
        session_id: useSessionIdStore.getState().session_id,
      }),
    });

    if (!response.ok) {
      logger.error('approvalApi', 'Failed to mark approval as expired', { status: response.status, approvalId });
    }
  } catch (error) {
    logger.error('approvalApi', 'Error marking approval as expired', { error: error.message, approvalId });
  }
};
